import React, { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import useTheme from '@mui/material/styles/useTheme';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { manualPurchase, createPurchase, hasPendingPayments } from 'services/purchasesService';
import { uploadFile } from 'services/storageService';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import useMediaQuery from '@mui/material/useMediaQuery';
import PaymentIcon from '@mui/icons-material/Payment';
import { logPurchaseError } from '../services/errorService';

export default function PurchaseModal({ open, onClose, prize }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [quantity, setQuantity] = useState(2);
  const [form, setForm] = useState({ cedula: '', nombre: '', correo: '', telefono: '', referencia: '' });
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loadingMessage, setLoadingMessage] = useState('');
  const fileInputRef = useRef(null);
  const cedulaInputRef = useRef();
  const [showPaymentBox, setShowPaymentBox] = useState(true);

  // Reset all fields to their initial state
  const resetFields = () => {
    setQuantity(2);
    setForm({ cedula: '', nombre: '', correo: '', telefono: '', referencia: '' });
    setErrors({});
    setFile(null);
    setFileError('');
    setError(null);
    setLoading(false);
    setShowPaymentBox(true);
  };

  // Clear fields whenever modal is opened or closed
  useEffect(() => {
    resetFields();
  }, [open]);

  // Field validation
  const validateField = (field, value) => {
    let message = '';
    if (!value) message = 'Campo obligatorio';
    else if (field === 'cedula' && !/^\d{7,9}$/.test(value)) message = 'Cédula inválida (7-9 dígitos)';
    else if (field === 'correo' && !/^\S+@\S+\.\S+$/.test(value)) message = 'Email inválido';
    else if (field === 'telefono' && !/^\+?[\d\s-]{10,15}$/.test(value)) message = 'Teléfono inválido (ej: +584141234567 o 04141234567)';
    else if (field === 'referencia' && !/^\d+$/.test(value)) message = 'Referencia numérica';
    setErrors(prev => ({ ...prev, [field]: message }));
  };

  const price = prize?.price || 15;
  const percentAvailable = prize?.percentAvailable || 0;
  const finalPrice = percentAvailable <= 20 ? price : price;
  const total = finalPrice * quantity;
  const maxTickets = prize?.ticketsCount || 0;

  
  // console.log('maxTickets', maxTickets);
  // console.log('percentAvailable', percentAvailable);
  // console.log('finalPrice', finalPrice);
  // console.log('total', total);
  const handleCopy = () => {
    // Copy payment info with total amount (2 decimals)
    const amount = total.toFixed(2);
    const text = `0102 BDV 26127594 0412-0381128 ${amount}`;
    navigator.clipboard.writeText(text);
    // Hacer focus en el input de cédula
    if (cedulaInputRef.current) {
      cedulaInputRef.current.focus();
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Verificar si hay pagos pendientes
      const result = await hasPendingPayments(form.cedula);
      if (result.hasPending) {
        setError({
          type: 'warning',
          message: result.message,
          icon: <WarningIcon sx={{ fontSize: 40 }} />
        });
        setLoading(false);
        return;
      }

      if (quantity > maxTickets) {
        setError({
          type: 'error',
          message: `Solo hay ${maxTickets} tickets disponibles`,
          icon: <ErrorIcon sx={{ fontSize: 40 }} />
        });
        setLoading(false);
        return;
      }
      // Validate all form fields
      const newErrors = {};
      Object.entries(form).forEach(([field, value]) => {
        if (!value) newErrors[field] = 'Campo obligatorio';
        else if (field === 'cedula' && !/^\d{7,9}$/.test(value)) newErrors[field] = 'Cédula inválida (7-9 dígitos)';
        else if (field === 'correo' && !/^\S+@\S+\.\S+$/.test(value)) newErrors[field] = 'Email inválido';
        else if (field === 'telefono' && !/^\+?[\d\s-]{10,15}$/.test(value)) newErrors[field] = 'Teléfono inválido (ej: +584141234567 o 04141234567)';
        else if (field === 'referencia' && !/^\d+$/.test(value)) newErrors[field] = 'Referencia numérica';
      });
      if (Object.values(newErrors).some(msg => msg)) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }
      if (!file) {
        setError({
          type: 'error',
          message: 'Debes subir un comprobante de pago',
          icon: <ErrorIcon sx={{ fontSize: 40 }} />
        });
        setLoading(false);
        return;
      }
      setLoadingMessage('Procesando tu compra...');
      let receiptUrl = null;
      try {
        const { url } = await uploadFile(file);
        receiptUrl = url;
      } catch (uploadError) {
        console.error('Error al subir comprobante:', uploadError);
        const msg = uploadError.message || 'Error al subir comprobante';
        setError({
          type: 'error',
          message: msg,
          icon: <ErrorIcon sx={{ fontSize: 40 }} />
        });
        setSnackbarMessage(msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
        setLoadingMessage('');
        return;
      }
      try {
        setLoadingMessage('Registrando tu compra y asignando tickets... (Puede tardar unos minutos, segun la cantidad de tickets)');
        const purchaseId = await manualPurchase(
          prize.id,
          { 
            cedula: form.cedula, 
            nombre: form.nombre, 
            correo: form.correo, 
            telefono: form.telefono, 
            referencia: form.referencia, 
            totalAmount: Number(total), 
            receiptUrl 
          },
          quantity
        );
        setLoadingMessage('Enviando confirmación por correo...');
        const successMsg = '¡Compra registrada con éxito! Por favor, verifica tu bandeja de correo electrónico donde recibirás la confirmación de tu compra.';
        setSnackbarMessage(successMsg);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        onClose();
      } catch (e) {
        console.error('Error al procesar la compra:', e);
        
      
      // Registrar el error con información detallada
      await logPurchaseError(e, {
        rifaId: prize.id,
        prizeTitle: prize.title,
        quantity,
        totalAmount: Number(total),
        receiptUrl,
        reference: form.referencia,
        status: 'error'
      }, {
        cedula: form.cedula,
        nombre: form.nombre,
        correo: form.correo,
        telefono: form.telefono
      });

        let errorMessage = e.message;
        
        // Manejar específicamente el error de índice de Firestore
        if (e.message && e.message.includes('requires an index')) {
          errorMessage = 'Error en la configuración del sistema. Por favor, contacta al administrador.';
        }
        
        setError({
          type: 'error',
          message: errorMessage,
          icon: <ErrorIcon sx={{ fontSize: 40 }} />
        });
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    } catch (error) {
    // Registrar errores de validación
    await logError(error, {
      type: 'validation_error',
      formData: {
        ...form,
        quantity,
        totalAmount: Number(total)
      }
    });
      setError({
        type: 'error',
        message: error.message,
        icon: <ErrorIcon sx={{ fontSize: 40 }} />
      });
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const isFormValid = () => {
    return (
      form.cedula &&
      form.nombre &&
      form.correo &&
      form.telefono &&
      form.referencia &&
      !Object.values(errors).some(error => error) &&
      file
    );
  };

  const validateFile = (file) => {
    setFileError('');
    
    // Verificar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setFileError('Solo se permiten archivos de imagen');
      return false;
    }

    // Verificar tamaño (máximo 5MB)
    const maxSize = 100 * 1024 * 1024; // 100MB en bytes
    if (file.size > maxSize) {
      setFileError('La imagen no debe superar los 100MB');
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
    } else {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
            color: '#fff',
            textShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)',
          }}
        >
          Comprar Tickets - {prize?.title}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
          {/* Sección de selección de tickets - Ahora es la primera sección */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: theme.palette.primary.light + '10',
                border: `2px solid ${theme.palette.primary.main}`,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 'bold' }}>
                ¡Primero selecciona la cantidad de tickets!
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton 
                    onClick={() => setQuantity(Math.max(2, quantity - 1))} 
                    disabled={quantity <= 2}
                    sx={{ 
                      color: quantity <= 2 ? theme.palette.grey[400] : theme.palette.error.main,
                      border: `2px solid ${quantity <= 2 ? theme.palette.grey[400] : theme.palette.error.main}`,
                      '&:hover': {
                        backgroundColor: theme.palette.error.light + '20'
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    type='number'
                    value={quantity}
                    onChange={e => setQuantity(Math.max(2, parseInt(e.target.value) || 2))}
                    inputProps={{ 
                      min: 2, 
                      max: maxTickets, 
                      style: { 
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      } 
                    }}
                    sx={{ 
                      width: 100,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.dark,
                        },
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => setQuantity(Math.min(maxTickets, quantity + 1))}
                    disabled={quantity >= maxTickets}
                    sx={{ 
                      color: quantity >= maxTickets ? theme.palette.grey[400] : theme.palette.success.main,
                      border: `2px solid ${quantity >= maxTickets ? theme.palette.grey[400] : theme.palette.success.main}`,
                      '&:hover': {
                        backgroundColor: theme.palette.success.light + '20'
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                <Typography variant="h5" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                {percentAvailable <= 20 && (
                  <Typography variant="h4" sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>
                    {finalPrice} Bs x {quantity} = {total.toFixed(2)} Bs
                  </Typography>
                )}
                {percentAvailable > 20 && (
                  <Typography variant="h5" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                    {price} Bs x {quantity} = {total.toFixed(2)} Bs
                  </Typography>
                )}
                
                {percentAvailable <= 20 && (
                  <Typography
                      component="span" 
                      sx={{ 
                        ml: 1, 
                        color: theme.palette.success.main,
                        fontSize: '1.5em',
                        fontWeight: 'bold'
                      }}
                    >
                      ¡PRECIO ESPECIAL!
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Cantidad mínima: 2 tickets
                </Typography>
                {quantity >= maxTickets && (
                  <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                    Máximo disponible: {maxTickets} tickets
                  </Typography>
                )}
              </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              {isMobile ? (
                <>
                  {/* <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowPaymentBox(!showPaymentBox)}
                    startIcon={<PaymentIcon />}
                    sx={{
                      mb: 2,
                      borderRadius: '0.5rem',
                      border: '2px solid transparent',
                      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                      backgroundOrigin: 'padding-box, border-box',
                      backgroundClip: 'padding-box, border-box',
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        filter: 'brightness(1.1)',
                        borderRadius: '0.5rem',
                        border: '2px solid transparent',
                        backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                        backgroundOrigin: 'padding-box, border-box',
                        backgroundClip: 'padding-box, border-box',
                        color: theme.palette.text.secondary,
                      },
                    }}
                  >
                    {showPaymentBox ? 'Ocultar datos de pago' : 'Mostrar datos de pago'}
                  </Button> */}
                  {showPaymentBox && (
                    <Box sx={{ textAlign: 'center', padding: 2, borderRadius: 2, backgroundColor: theme.palette.grey[100] }}>
                      <Typography variant='inherit'><strong>Pago Móvil</strong></Typography>
                      <Typography variant='inherit'>Banco: <strong>0102 BDV</strong></Typography>
                      <Typography variant='inherit'>Cédula: <strong>26127594</strong></Typography>
                      <Typography variant='inherit'>Tlf: <strong>0412-0381128</strong></Typography>
                      <Button
                        onClick={handleCopy}
                        variant="outlined"
                        sx={{
                          mt: theme.spacing(1),
                          borderRadius: '0.5rem',
                          border: '2px solid transparent',
                          backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                          backgroundOrigin: 'padding-box, border-box',
                          backgroundClip: 'padding-box, border-box',
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            filter: 'brightness(1.1)',
                            borderRadius: '0.5rem',
                            border: '2px solid transparent',
                            backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                            backgroundOrigin: 'padding-box, border-box',
                            backgroundClip: 'padding-box, border-box',
                            color: theme.palette.text.secondary,
                          },
                        }}
                      >
                        Copiar datos
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', padding: 2, borderRadius: 2, backgroundColor: theme.palette.grey[100] }}>
                  <Typography variant='inherit'><strong>Pago Móvil</strong></Typography>
                  <Typography variant='inherit'>Banco: <strong>0102 BDV</strong></Typography>
                  <Typography variant='inherit'>Cédula: <strong>26127594</strong></Typography>
                  <Typography variant='inherit'>Tlf: <strong>0412-0381128</strong></Typography>
                  <Button
                    onClick={handleCopy}
                    variant="outlined"
                    sx={{
                      mt: theme.spacing(1),
                      borderRadius: '0.5rem',
                      border: '2px solid transparent',
                      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                      backgroundOrigin: 'padding-box, border-box',
                      backgroundClip: 'padding-box, border-box',
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        filter: 'brightness(1.1)',
                        borderRadius: '0.5rem',
                        border: '2px solid transparent',
                        backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                        backgroundOrigin: 'padding-box, border-box',
                        backgroundClip: 'padding-box, border-box',
                        color: theme.palette.text.secondary,
                      },
                    }}
                  >
                    Copiar datos
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              {/* User Details & Upload */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label='Cédula'
                    value={form.cedula}
                    required
                    inputProps={{ inputMode: 'numeric', pattern: '\\d{7,9}', maxLength: 9 }}
                    error={!!errors.cedula}
                    helperText={errors.cedula}
                    onChange={e => { setForm(prev => ({ ...prev, cedula: e.target.value })); validateField('cedula', e.target.value); }}
                    fullWidth
                    inputRef={cedulaInputRef}
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label='Nombre completo'
                    value={form.nombre}
                    required
                    error={!!errors.nombre}
                    helperText={errors.nombre}
                    onChange={e => { setForm(prev => ({ ...prev, nombre: e.target.value })); validateField('nombre', e.target.value); }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label='Correo'
                    type='email'
                    value={form.correo}
                    required
                    error={!!errors.correo}
                    helperText={errors.correo}
                    onChange={e => { setForm(prev => ({ ...prev, correo: e.target.value })); validateField('correo', e.target.value); }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label='Teléfono'
                    value={form.telefono}
                    required
                    inputProps={{ 
                      inputMode: "numeric", 
                      pattern: "[+]?[0-9\\s-]{10,15}", 
                      maxLength: 15, 
                      title: "Ej: +584141234567 o 04141234567" 
                    }}
                    error={!!errors.telefono}
                    helperText={errors.telefono}
                    onChange={(e) => { 
                      setForm((prev) => ({ ...prev, telefono: e.target.value })); 
                      validateField("telefono", e.target.value); 
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed',
                      borderColor: file ? 'success.main' : 'error.main',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: file ? 'success.light' : 'error.light',
                      color: file ? 'success.main' : 'white',
                      '&:hover': {
                        backgroundColor: file ? 'success.lighter' : 'error.lighter',
                      },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <input
                      type='file'
                      hidden
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                    <CloudUploadIcon
                      sx={{
                        fontSize: '2rem',
                        width: '2em',
                        height: '2em',
                        color: file ? '#FFF' : '#FFF',
                        transition: 'color 0.3s',
                      }}
                    />
                    <Typography variant='caption' color={file ? '#FFF' : '#FFF'}>
                      {file ? 'Comprobante seleccionado' : 'Arrastra y suelta o haz clic para subir comprobante (Obligatorio)'}
                    </Typography>
                    {file && (
                      <Typography variant='caption' color='textPrimary'>
                        {file.name}
                      </Typography>
                    )}
                    {fileError && (
                      <Typography variant='caption' color='error' sx={{ display: 'block', mt: 1 }}>
                        {fileError}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='Número de referencia'
                    value={form.referencia}
                    required
                    error={!!errors.referencia}
                    helperText={errors.referencia}
                    onChange={e => { setForm(prev => ({ ...prev, referencia: e.target.value })); validateField('referencia', e.target.value); }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2 }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            align="center" 
            sx={{ mb: 2, fontStyle: 'italic' }}
          >
            Al realizar la compra, aceptas los términos y condiciones de la plataforma.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              color="error"
              sx={{
                color: 'red',
                borderColor: 'red',
                '&:hover': { filter: 'brightness(1.1)' },
              }}
            >
              Cancelar
            </Button>
            {maxTickets > 0 ? (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading || quantity > maxTickets || !isFormValid()}
                sx={{
                  background: 'linear-gradient(90deg, #830095 0%, #830095 33%, #830095 66%, #830095 100%)',
                  color: '#fff',
                  textShadow: '0px 1px 2px rgba(0,0,0,0.5)',
                  '&:hover': { filter: 'brightness(1.1)' },
                  minWidth: '120px',
                  position: 'relative',
                  '&.Mui-disabled': {
                    background: 'linear-gradient(90deg, #e0e0e0 0%, #f5f5f5 100%)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{
                        color: '#fff',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                    <span style={{ visibility: 'hidden' }}>Comprar</span>
                  </>
                ) : !isFormValid() ? (
                  'Completa los campos'
                ) : (
                  'Comprar'
                )}
              </Button>
            ) : (
              <Typography color="error" variant="body2">
                No hay tickets disponibles
              </Typography>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          elevation={6} 
          variant="filled"
          sx={{
            '& .MuiAlert-message': {
              color: '#fff'
            }
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" color="inherit">
            {loadingMessage}
          </Typography>
          {loadingMessage === 'Enviando confirmación por correo...' && (
            <Typography variant="body2" color="inherit" sx={{ textAlign: 'center', maxWidth: '80%' }}>
              Tu compra se ha registrado correctamente y está en espera de aprobación.
              <br />
              Por favor, revisa tu carpeta de spam para encontrar el correo de confirmación.
            </Typography>
          )}
        </Box>
      </Backdrop>
      {error && (
        <Dialog
          open={!!error}
          onClose={() => setError(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
            }
          }}
        >
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: error.type === 'warning' ? 'warning.light' : 'error.light',
                color: error.type === 'warning' ? 'warning.dark' : 'error.dark',
                mb: 2
              }}
            >
              {error.icon}
            </Box>
            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold' }}>
              {error.type === 'warning' ? 'Atención' : 'Error'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {error.message}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setError(null)}
              sx={{
                background: error.type === 'warning' ? 'warning.main' : 'error.main',
                color: '#ffffff',
                fontWeight: 'bold',
                textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                '&:hover': {
                  background: error.type === 'warning' ? 'warning.dark' : 'error.dark',
                  color: '#ffffff',
                }
              }}
            >
              Entendido
            </Button>
          </Box>
        </Dialog>
      )}
    </>
  );
}